import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LowStockProvider } from './contexts/LowStockContext';
import { Alert, Container, Typography } from '@mui/material';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminSetup from './pages/AdminSetup';
import FirestoreErrorBoundary from './components/FirestoreErrorBoundary';
import { onAuthChange, logoutUser } from './firebase/authService';
import { getUserPermissions } from './firebase/userService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase/config';
import useFirestoreStore from './stores/useFirestoreStore';

function App() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminExists, setAdminExists] = useState(null);
  
  // Get Firestore store methods
  const subscribe = useFirestoreStore((state) => state.subscribe);
  const unsubscribeAll = useFirestoreStore((state) => state.unsubscribeAll);

  // Initialize ALL Firestore listeners once when app mounts
  useEffect(() => {
    console.log('→ Initializing global Firestore listeners');
    
    // Subscribe to all collections that the app needs
    // These listeners will persist across all route changes
    const collections = [
      'bills',
      'customers',
      'inventory',
      'staff',
      'users',
      'expenses',
      'expenseCategories',
      'creditors',
      'returns',
      'activityLogs',
      'suppliers',
      'purchaseOrders',
      'grns',
      'stockTransfers',
      'tradePayables',
      'tradeReceivables'
    ];
    
    collections.forEach(collectionName => {
      subscribe(collectionName, {
        orderByField: 'createdAt',
        orderDirection: 'desc'
      });
    });
    
    console.log('✓ All Firestore listeners initialized');
    
    // Cleanup ALL listeners only when app unmounts
    return () => {
      console.log('→ App unmounting, cleaning up all listeners');
      unsubscribeAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount, subscribe/unsubscribeAll are stable

  useEffect(() => {
    let unsubscribe;

    const checkAdminAndAuth = async () => {
      try {
        if (!db) {
          console.error('Firebase is not initialized. Please check your .env.local file.');
          setAdminExists(false);
          setLoading(false);
          return;
        }

        // Check if admin exists
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        
        let hasAdmin = false;
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.userType === 'admin' || userData.role === 'admin') {
            hasAdmin = true;
          }
        });
        
        setAdminExists(hasAdmin);

        // Setup auth listener
        unsubscribe = onAuthChange(async (firebaseUser) => {
          if (window.__suppressAuthStateChange) {
            return;
          }
          
          if (firebaseUser) {
            const permsResult = await getUserPermissions(firebaseUser.uid);
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              role: permsResult.data?.userType || 'staff',
            });
            setPermissions(permsResult.data || {});
          } else {
            setUser(null);
            setPermissions(null);
          }
          setLoading(false);
        });
      } catch (err) {
        console.error('Error checking admin:', err);
        setAdminExists(true);
        setLoading(false);
      }
    };

    checkAdminAndAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleLogin = async (userData) => {
    const permsResult = await getUserPermissions(userData.uid);
    setUser(userData);
    setPermissions(permsResult.data || {});
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
    setPermissions(null);
  };

  // When loading finishes, hide the HTML initial loader
  useEffect(() => {
    if (!loading && typeof window.hideInitialLoader === 'function') {
      window.hideInitialLoader();
    }
  }, [loading]);

  if (loading) {
    // Keep HTML loader visible — return null so React renders nothing
    return null;
  }

  // Show admin setup if no admin exists
  if (adminExists === false) {
    if (!db) {
      return (
        <ThemeProvider>
          <Container maxWidth="md" sx={{ mt: 8 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Firebase Configuration Missing
              </Typography>
              <Typography variant="body2" paragraph>
                The application cannot connect to Firebase because environment variables are not configured.
              </Typography>
              <Typography variant="body2" component="div">
                To fix this:
                <ol style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <li>Create a <code>.env.local</code> file in the project root</li>
                  <li>Copy the contents from <code>.env.example</code></li>
                  <li>Update the values with your Firebase project credentials</li>
                  <li>Restart the development server</li>
                </ol>
              </Typography>
            </Alert>
          </Container>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider>
        <LowStockProvider>
          <AdminSetup onSetupComplete={() => setAdminExists(true)} />
        </LowStockProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <LowStockProvider>
        <FirestoreErrorBoundary>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  user ?
                    <Navigate to="/dashboard" replace /> :
                    <Login onLogin={handleLogin} />
                }
              />
              <Route
                path="/"
                element={
                  user ?
                    <Navigate to="/dashboard" replace /> :
                    <Navigate to="/login" replace />
                }
              />
              <Route
                path="/*"
                element={
                  user ?
                    <Dashboard user={user} permissions={permissions} onLogout={handleLogout} /> :
                    <Navigate to="/login" replace />
                }
              />
            </Routes>
          </Router>
        </FirestoreErrorBoundary>
      </LowStockProvider>
    </ThemeProvider>
  );
}

export default App;
